/**
 * Natural-language meal parsing.
 *
 * "two eggs, toast, black coffee" -> three structured items with quantities.
 *
 * This runs entirely on-device against the bundled USDA data. It is the
 * PRIMARY path, not a fallback: logging has to work on a plane, in a lift, and
 * in the two seconds of attention a meal actually gets. An optional LLM pass
 * can refine the same structure later (see data/llm.ts), but nothing here
 * depends on it.
 *
 * The parser deliberately does not guess when it cannot resolve an amount. An
 * unresolved item is returned with `needsAmount`, so the UI can ask one
 * question instead of silently logging a wrong number.
 */

const WORD_NUMBERS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, dozen: 12,
  half: 0.5, quarter: 0.25, couple: 2, few: 3,
};

const VULGAR_FRACTIONS: Record<string, number> = {
  '½': 0.5, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 0.25, '¾': 0.75, '⅛': 0.125,
};

/** Units we recognise. Mass and count units are resolvable; volume often is not. */
const UNIT_ALIASES: Record<string, string> = {
  g: 'g', gram: 'g', grams: 'g', gs: 'g',
  kg: 'kg', kilogram: 'kg', kilograms: 'kg',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  ml: 'ml', millilitre: 'ml', milliliter: 'ml',
  l: 'l', litre: 'l', liter: 'l',
  cup: 'cup', cups: 'cup',
  tbsp: 'tbsp', tablespoon: 'tbsp', tablespoons: 'tbsp',
  tsp: 'tsp', teaspoon: 'tsp', teaspoons: 'tsp',
  slice: 'slice', slices: 'slice',
  piece: 'piece', pieces: 'piece',
  scoop: 'scoop', scoops: 'scoop',
  serving: 'serving', servings: 'serving',
  can: 'can', cans: 'can',
  bottle: 'bottle', bottles: 'bottle',
  large: 'large', medium: 'medium', small: 'small',
  handful: 'handful',
};

/** Words that carry no food meaning and only confuse matching. */
const FILLER = new Set([
  'of', 'with', 'and', 'some', 'a', 'an', 'the', 'plus', 'my', 'had',
  'ate', 'eaten', 'for', 'then', 'also',
]);

export interface ParsedItem {
  /** Amount, if one was stated or implied. */
  qty: number;
  /** Normalised unit, or null when the amount is a bare count ("2 eggs"). */
  unit: string | null;
  /** The food text, cleaned of quantity and filler. */
  query: string;
  /** The original fragment, kept so the user can see what was interpreted. */
  raw: string;
  /** True when no amount was stated and 1 was assumed. */
  assumedQty: boolean;
}

/** Split a meal description into one fragment per food. */
export function splitItems(text: string): string[] {
  return text
    .split(/\s*(?:,|;|\band\b|\bwith\b|\+|\n|\/(?=\s))\s*/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Read a leading quantity: "2", "1.5", "1/2", "½", "two", "a". */
function readQuantity(tokens: string[]): { qty: number; consumed: number; stated: boolean } {
  const first = tokens[0]?.toLowerCase();
  if (!first) return { qty: 1, consumed: 0, stated: false };

  // Mixed number: "1 1/2"
  const second = tokens[1];
  if (/^\d+$/.test(first) && second && /^\d+\/\d+$/.test(second)) {
    const [n, d] = second.split('/').map(Number);
    if (d) return { qty: Number(first) + n! / d, consumed: 2, stated: true };
  }
  if (/^\d+\/\d+$/.test(first)) {
    const [n, d] = first.split('/').map(Number);
    if (d) return { qty: n! / d, consumed: 1, stated: true };
  }
  if (VULGAR_FRACTIONS[first] !== undefined) {
    return { qty: VULGAR_FRACTIONS[first]!, consumed: 1, stated: true };
  }
  // "200g" written without a space is handled by the unit reader; here take
  // only a bare number.
  if (/^\d+(\.\d+)?$/.test(first)) {
    return { qty: Number(first), consumed: 1, stated: true };
  }
  if (WORD_NUMBERS[first] !== undefined) {
    // "a" and "an" are articles as often as they are the number one; treat
    // them as an unstated quantity so the UI does not claim the user said 1.
    const stated = first !== 'a' && first !== 'an';
    return { qty: WORD_NUMBERS[first]!, consumed: 1, stated };
  }
  return { qty: 1, consumed: 0, stated: false };
}

/** Pull "200g" apart into 200 and "g". */
function splitGlued(token: string): { qty: number; unit: string } | null {
  const m = /^(\d+(?:\.\d+)?)([a-z]+)$/i.exec(token);
  if (!m) return null;
  const unit = UNIT_ALIASES[m[2]!.toLowerCase()];
  if (!unit) return null;
  return { qty: Number(m[1]), unit };
}

/** Parse one fragment into a structured item. */
export function parseItem(fragment: string): ParsedItem | null {
  const raw = fragment.trim();
  if (!raw) return null;

  let tokens = raw.split(/\s+/).filter(Boolean);
  let qty = 1;
  let stated = false;
  let unit: string | null = null;

  const glued = splitGlued(tokens[0] ?? '');
  if (glued) {
    qty = glued.qty;
    unit = glued.unit;
    stated = true;
    tokens = tokens.slice(1);
  } else {
    const read = readQuantity(tokens);
    qty = read.qty;
    stated = read.stated;
    tokens = tokens.slice(read.consumed);

    const maybeUnit = tokens[0]?.toLowerCase().replace(/[.,]$/, '');
    if (maybeUnit && UNIT_ALIASES[maybeUnit]) {
      // "large" only reads as a unit when something follows it: "2 large eggs"
      // has the unit, "a large" alone does not.
      const isSizeWord = ['large', 'medium', 'small'].includes(UNIT_ALIASES[maybeUnit]!);
      if (!isSizeWord || tokens.length > 1) {
        unit = UNIT_ALIASES[maybeUnit]!;
        tokens = tokens.slice(1);
      }
    }
  }

  const query = tokens
    .map((t) => t.replace(/[.,;]$/, ''))
    .filter((t) => !FILLER.has(t.toLowerCase()))
    .join(' ')
    .trim();

  if (!query) return null;
  return { qty, unit, query, raw, assumedQty: !stated };
}

/** Parse a whole meal description. */
export function parseMeal(text: string): ParsedItem[] {
  return splitItems(text)
    .map(parseItem)
    .filter((x): x is ParsedItem => x !== null);
}
